<script setup lang="ts">
import {computed, ref} from "vue";
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import ChatPresenter from "@/utils/presenters/ChatPresenter.ts";
import {QBtn} from "quasar";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import DTOChatParticipant = namaTypes.DTOChatParticipant;
import EntityReferenceData = namaTypes.EntityReferenceData;
import QuestionField = namaTypes.QuestionField;

const props = defineProps<{
  presenter: ChatPresenter;
  users: EntityReferenceData[];
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'created'): void;
}>();
const searchValue = ref();
const participants = ref<any[]>([]);
const filteredUsers = computed(() => {
  if (!props.users) return [];
  const search = searchValue.value?.toLowerCase()?.trim();
  if (!search) return props.users;
  return props.users.filter(user => {
    const searchableFields = [user.id, user.name1, user.name2, user.code, user.actualCode];
    return searchableFields.some(field =>
      field?.toString().toLowerCase().includes(search)
    );
  });
});

async function openCreateChatGroupDialog() {
  let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (record: Record<string, any>) => {
    let groupName = record['groupName'];
    await props.presenter.createConversationAndOpen('Group', participants.value, groupName ?? '');
    participants.value = [];
    emit('created');
  });
  let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
  });
  let questionField: QuestionField = NamaCommon.createQuestionField("groupName", FieldType.Text);
  let dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateId("createNewGroup"), true);
  await dialogBox.createPresenter(props.presenter, [questionField], []);
  await dialogBox.runDialogBox();
}

async function onCreateClick() {
  await openCreateChatGroupDialog()
}

function onItemClick(user: DTOChatParticipant) {
  if (!user || !user.id)
    return;
  if (participants.value.includes(user))
    participants.value.splice(participants.value.indexOf(user), 1);
  else
    participants.value.push(user);
}
</script>

<template>
  <div class="tw-flex tw-flex-col tw-min-h-0 tw-grow tw-w-full">
    <div class="tw-flex-none tw-shrink-0 tw-w-full">
      <q-list class="tw-w-full">
        <q-item class="tw-py-0">
          <div class="tw-flex tw-flex-row tw-self-center tw-w-full tw-gap-1.5">
            <q-btn unelevated no-caps dense
                   @click="emit('back')"
                   :title="Translator.translateId('back')"
                   class="tw-p-1 tw-rounded-md tw-text-namaFontColor-input-gray dark:tw-text-namaFontColor-input-dark tw-w-fit">
              <NamaIcon default-icon="arrow_back" class="tw-size-5 !tw-text-xl" :class="[Translator.isArabic()?'tw-rotate-180':'']"/>
            </q-btn>
            <div class="tw-w-full tw-font-semibold">
              <span v-text="Translator.translateId('createNewGroup')"/>
            </div>
          </div>
        </q-item>
      </q-list>
    </div>
    <q-item class="tw-py-0">
      <div class="tw-relative tw-leading-tight tw-w-full tw-h-full">
                <span class="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-justify-center tw-items-center tw-pl-3">
                  <NamaIcon defaultIcon="search"/>
                </span>
        <input type="text" :placeholder="Translator.translateId('search')" v-model="searchValue"
               class="tw-border-none tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark
                       dark:hover:tw-bg-namaBackground-bright-dark hover:tw-bg-namaBackground-bright-light
                       dark:focus:tw-bg-namaBackground-bright-dark focus:tw-bg-namaBackground-bright-light
                        tw-shadow tw-appearance-none tw-rounded-md tw-w-full tw-py-2 tw-pl-12  tw-text-namaFontColor-input-light dark:tw-text-namaFontColor-input-dark
                  focus:tw-ring-0 focus:tw-outline-none focus:tw-shadow-outline dark:tw-placeholder-gray-300" autocomplete="off"
        />
      </div>
    </q-item>
    <div class="tw-grow tw-overflow-y-auto tw-w-full tw-min-h-0 tw-pb-3">
      <template v-for="(user, index) in filteredUsers" :key="index">
        <q-item clickable :active="true" @click.stop="onItemClick(user)">
          <q-item-section avatar>
            <q-checkbox v-model="participants" :val="user" color="primary"/>
          </q-item-section>
          <q-item-section avatar>
            <q-avatar color="primary">
              <img :src="LoginUtils.getUserImageSrc(user.id ?? '', false)" alt="">
            </q-avatar>
          </q-item-section>
          <q-item-section v-text="Translator.translateRef(user)"/>
        </q-item>
        <q-separator/>
      </template>
    </div>
    <q-item class="tw-h-fit tw-min-h-fit tw-justify-center">
      <q-btn
        v-if="participants.length > 0"
        :label="Translator.translateId('create')"
        dense unelevated no-caps @click="onCreateClick"
        class="tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white tw-text-base tw-font-normal tw-rounded-md tw-w-1/2 tw-h-fit"/>
    </q-item>
  </div>
</template>
